import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaUniversity } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const POTDCalculator = () => {
  const [principal, setPrincipal] = useState(100000);
  const [tenure, setTenure] = useState(5);
  const [result, setResult] = useState(null);

  // Post Office Time Deposit Interest Rates (Q1 FY 2024-25)
  const interestRates = {
    1: 6.9,
    2: 7.0,
    3: 7.1,
    5: 7.5,
  };

  const tenureOptions = [
    { years: 1, label: '1 Year' },
    { years: 2, label: '2 Years' },
    { years: 3, label: '3 Years' },
    { years: 5, label: '5 Years' },
  ];

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, tenure]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const rate = interestRates[tenure] / 100;
    
    // Post Office TD is compounded quarterly
    const n = 4; // quarterly
    const t = tenure;
    
    const maturityAmount = principal * Math.pow(1 + rate / n, n * t);
    const totalInterest = maturityAmount - principal;
    
    // Generate yearly breakdown
    const yearlyData = [];
    let balance = principal;
    
    for (let year = 1; year <= tenure; year++) {
      // openingBalance tracked but used in yearlyData object
      // eslint-disable-next-line no-unused-vars
      const openingBalance = balance;
      balance = principal * Math.pow(1 + rate / n, n * year);
      const interest = balance - (year === 1 ? principal : yearlyData[year - 2].closingBalance);
      
      yearlyData.push({
        year,
        openingBalance: Math.round(year === 1 ? principal : yearlyData[year - 2].closingBalance),
        interest: Math.round(interest),
        closingBalance: Math.round(balance),
      });
    }
    
    // 80C benefit only for 5-year TD
    const taxBenefit = tenure === 5 ? Math.min(principal, 150000) : 0;
    
    setResult({
      maturityAmount: Math.round(maturityAmount),
      totalInterest: Math.round(totalInterest),
      interestRate: interestRates[tenure],
      taxBenefit,
      yearlyData,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Post Office Time Deposit Report', {
      'Principal Amount': formatCurrency(principal),
      'Tenure': `${tenure} Year(s)`,
      'Interest Rate': `${result.interestRate}% p.a.`,
      'Compounding': 'Quarterly',
      'Maturity Amount': formatCurrency(result.maturityAmount),
      'Total Interest': formatCurrency(result.totalInterest),
      'Section 80C Benefit': tenure === 5 ? formatCurrency(result.taxBenefit) : 'Not Applicable',
    });
  };

  const handleReset = () => {
    setPrincipal(100000);
    setTenure(5);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Post Office TD Calculator"
        description="Free Post Office Time Deposit Calculator - Calculate POTD returns for 1-5 year tenure."
        keywords="POTD calculator, Post Office Time Deposit calculator, post office FD calculator, POTD interest rate, time deposit calculator"
        canonicalPath="/potd"
      />
      <div className="calculator-header">
        <h1><FaUniversity style={{ color: '#dc2626' }} /> Post Office Time Deposit (POTD)</h1>
        <p>Calculate returns for Post Office Fixed Deposit with sovereign guarantee</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Investment Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                min="1000"
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>Min: ₹1,000, In multiples of ₹100</small>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="1000000"
                step="1000"
                value={principal || 0}
                onChange={(e) => setPrincipal(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Select Tenure</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' }}>
              {tenureOptions.map((option) => (
                <button
                  key={option.years}
                  onClick={() => setTenure(option.years)}
                  style={{
                    padding: '16px 8px',
                    border: tenure === option.years ? '2px solid #dc2626' : '1px solid var(--border)',
                    borderRadius: '8px',
                    background: tenure === option.years ? '#fef2f2' : 'var(--surface)',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontWeight: tenure === option.years ? 'bold' : 'normal' }}>{option.label}</div>
                  <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                    {interestRates[option.years]}%
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}>
                <h3>Maturity Amount ({tenure} Year{tenure > 1 ? 's' : ''})</h3>
                <div className="amount">{formatCurrency(result.maturityAmount)}</div>
                <small>@ {result.interestRate}% p.a. (Quarterly Compounding)</small>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Invested</span>
                  <span className="value">{formatCurrency(principal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Earned</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.totalInterest)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Rate</span>
                  <span className="value">{result.interestRate}% p.a.</span>
                </div>
                <div className="result-item">
                  <span className="label">Effective Annual Return</span>
                  <span className="value">{((result.maturityAmount / principal) ** (1 / tenure) - 1) * 100}%</span>
                </div>
                {tenure === 5 && (
                  <div className="result-item" style={{ background: '#eff6ff' }}>
                    <span className="label">Section 80C Tax Benefit</span>
                    <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.taxBenefit)}</span>
                  </div>
                )}
              </div>

              {/* Year-wise Growth */}
              <div className="breakup-section">
                <h4>Year-wise Growth</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Opening Balance</th>
                      <th>Interest</th>
                      <th>Closing Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.yearlyData.map((row, index) => (
                      <tr key={index}>
                        <td>{row.year}</td>
                        <td>{formatCurrency(row.openingBalance)}</td>
                        <td style={{ color: '#10b981' }}>{formatCurrency(row.interest)}</td>
                        <td style={{ fontWeight: 'bold' }}>{formatCurrency(row.closingBalance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Interest Rate Comparison */}
              <div className="breakup-section" style={{ marginTop: '20px' }}>
                <h4>Current Interest Rates (Q1 FY 2024-25)</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Tenure</th>
                      <th>Interest Rate</th>
                      <th>80C Benefit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenureOptions.map((option) => (
                      <tr key={option.years} style={{ background: tenure === option.years ? '#fef2f2' : 'inherit' }}>
                        <td>{option.label}</td>
                        <td style={{ color: '#dc2626', fontWeight: 'bold' }}>{interestRates[option.years]}%</td>
                        <td>{option.years === 5 ? '✅ Yes' : '❌ No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Post Office Time Deposit Key Features</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li>Tenure: 1, 2, 3, or 5 years</li>
              <li>Minimum: ₹1,000, No maximum limit</li>
              <li>Interest compounded quarterly</li>
              <li>Sovereign guarantee by Govt of India</li>
              <li>Available at all Post Offices</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>5-year TD qualifies for 80C benefit</li>
              <li>Premature withdrawal after 6 months</li>
              <li>Can be pledged as loan collateral</li>
              <li>Auto-renewal facility available</li>
              <li>Higher rates than bank FDs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POTDCalculator;
